import { BaseModule } from '@core/base/module/base-module'
import { IUsersState } from './users'
import { unifiedFraudDefenseConfig } from './users.filters'
import { UnifiedFilterModule } from '@core/modules/filters/unified-filter.module'

const state = {
    data: [],
    last_fetched: 0,
    loading: false,
    error: '',
} satisfies IUsersState

class Users extends BaseModule<IUsersState> {
    filterModule: UnifiedFilterModule

    constructor(state: IUsersState) {
        super(state, 'users.module')
        this.filterModule = new UnifiedFilterModule(
            unifiedFraudDefenseConfig,
            'users.unified.filter'
        )
    }

    setData(data: TUserIssue[]) {
        this.store.setKey('data', data)
        this.store.setKey('last_fetched', Date.now())
        this.filterModule.initDynamicFilters(data)
    }

    setLoading(loading: boolean) {
        this.store.setKey('loading', loading)
    }

    setError(error: string) {
        this.store.setKey('error', error)
    }

    getUsersIssues({ disableFilters = false }) {
        const { data } = this.store.get()
        const processedData = this.filterModule.processData(
            data,
            disableFilters
        )
        return processedData
    }

    getFdIssues({ disableFilters = false }) {
        const { data } = this.store.get()
        const processedData = this.filterModule.processData(
            data,
            disableFilters
        )
        return processedData.filter(
            (issue: TIssue) => issue.issue_type === 'FRAUD_DEFENSE'
        )
    }

    getFdIssuesAsIssues(): TGeneralIssue[] {
        const { data } = this.useStore()
        const fdIssues = data.filter(
            (issue: TIssue) => issue.issue_type === 'FRAUD_DEFENSE'
        )
        return fdIssues.map((issue: TFraudDefenseIssue) => ({
            assets: issue.assets,
            email_address: issue.email_data.sender.email_address,
            id: issue.id,
            issue_category: issue.category,
            issue_type: issue.issue_type,
            long_description: issue.long_description,
            name: issue.name,
            ports: issue.ports,
            resolution_url: issue.resolution_url,
            severity: issue.severity,
            short_description: issue.short_description,
            status: issue.status,
            technologies: issue.technologies,
            email_data: issue.email_data,
            date_sent: issue.email_data.date_sent,
            recipients: issue.recipients,
        }))
    }

    getBecIssues(): TBECIssue[] {
        const { data } = this.useStore()
        return data.filter((issue: TIssue) => issue.issue_type === 'INBOX_RULE')
    }

    getBecIssuesAsIssues(): TBecIssueRow[] {
        return this.getBecIssues().map((issue) => ({
            assets: issue.assets,
            hostname: issue.hostname,
            id: issue.id,
            issue_category: issue.issue_category,
            issue_type: issue.issue_type,
            long_description: issue.long_description,
            name: issue.name,
            resolution_url: issue.resolution_url,
            severity: issue.severity,
            severity_score: issue.severity_score,
            short_description: issue.short_description,
            status: issue.status,
            rule_data: issue.rule_data,
            created_at: issue.created_at,
        }))
    }

    getUsersIssueById(id: string) {
        const { data } = this.store.get()
        return data.find((issue: TUserIssue) => issue.id === id)
    }
}

export const UsersModule = new Users(state)
export const unifiedUsersFilterModule = UsersModule.filterModule
export type TUsersModule = typeof UsersModule
